import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Home, MonitorCog, ClipboardList, PenLine, Zap, FilePenLine, BarChart3, Quote, Clock, TrendingDown, TrendingUp, Target, ShieldCheck, ShieldAlert, History, Activity, Flag, SearchCheck } from 'lucide-react';
import TitleFlowLogo from '../TitleFlowLogo';
import NotificationBell from './NotificationBell';
import UserProfileMenu from './UserProfileMenu';
import WorkspaceSwitcher from '../WorkspaceSwitcher';
import TeamActivityPanel from '../dashboard/TeamActivityPanel';
import { SessionHealthIndicator, BreakButton, SystemDowntimeButton } from '../session';
import { useAuth } from '../../App';
import { getDefaultRoute, isITRole, isAccountingRole } from '../../lib/dashboardViews';
import { WORKSPACE_NEUTRAL_PREFIXES, isWorkspaceNeutralPath, deriveWorkspaces, backToWorkspaceLink } from '../../utils/workspaces';
import { usePermissions } from '../../hooks/usePermissions';
import { getVisibleWorkspaceTabs } from '../hr/MyWorkspace';
import { TEAM_TABS } from '../hr/TeamManagement';
import { BREAK_MGMT_ALLOWED_ROLES } from '../BreakManagementProtectedRoute';

// UNIFIED-SHELL-001 — one persistent premium shell for every internal workspace (Analytics, My Workspace, Team,
// Break Management, Whiteboard, Auto Assignment, Edit Requests). Operations dashboard keeps the classic blue header.
// Sidebar visibility mirrors existing AppHeader / ReportsLayout / page tab rules — hiding is never the security control.
export const ShellContext = createContext(false);
export const useInShell = () => useContext(ShellContext);

// WS-CONTEXT-001: shell paths are workspace-neutral — single source in utils/workspaces.js.
export const SHELL_PATH_PREFIXES = WORKSPACE_NEUTRAL_PREFIXES;
export const isShellPath = isWorkspaceNeutralPath;

const HR_ROLES = ['hr_manager', 'hr_specialist', 'hr'];
const TEAM_ROLES = ['admin', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader', 'hr_manager', 'it_manager'];
const TEAM_LITE_ROLES = ['hr_manager', 'it_manager'];
const ANALYTICS_ROLES = ['admin', 'hr', 'hr_manager', 'hr_specialist', 'team_leader', 'tax_team_leader', 'title_team_leader', 'tax_search_team_leader', 'title_search_team_leader', 'manager', 'supervisor'];
const ANALYTICS_MGMT = ['admin', 'hr', 'hr_manager', 'hr_specialist', 'team_leader', 'tax_team_leader', 'title_team_leader', 'manager', 'supervisor'];
const TARGET_MANAGER_ROLES = ['admin', 'team_leader', 'tax_search_team_leader', 'title_search_team_leader'];

const byLabel = (a, b) => a.label.localeCompare(b.label);

// NAV-GROUPS-001 (Sep 2026): fixed group order Dashboard → My Workspace → Analytics → Break Management → Operations Tools →
// Team Management; items inside every group alphabetical. Pure re-grouping — routes, RBAC and visibility rules unchanged.
export const buildModuleNav = (user, workspaces = [], pathname = '') => {
  const role = user?.role;
  const sections = [];
  const item = (to, label, icon, testid, extra = {}) => ({ to, label, icon, testid, ...extra });

  if (role === 'admin') sections.push({ title: 'Dashboard', heading: false, items: [item('/admin', 'Home', Home, 'ms-nav-home', { end: true }), item('/dashboard', 'Operations', MonitorCog, 'ms-nav-operations', { end: true })] });
  else if (!isITRole(role) && !isAccountingRole(role) && !HR_ROLES.includes(role)) sections.push({ title: 'Dashboard', heading: false, items: [item(getDefaultRoute(role), 'Dashboard', ClipboardList, 'ms-nav-dashboard', { end: true })] });

  // WS-CONTEXT-002: dynamic "Back to <active workspace>" (its module home) — first item, never duplicates an existing Dashboard link.
  const back = role !== 'admin' && backToWorkspaceLink(workspaces, pathname, sections.flatMap(s => s.items.map(i => i.to)));
  if (back) {
    const backItem = item(back.to, back.label, ArrowLeft, 'ms-nav-back-workspace', { end: false, parentOf: 'path' });
    if (sections[0]?.title === 'Dashboard') sections[0].items.unshift(backItem);
    else sections.unshift({ title: 'Dashboard', heading: false, items: [backItem] });
  }

  if (!HR_ROLES.includes(role)) {
    // MY WORKSPACE is a heading only; its tabs are top-level items. /dashboard/workspace without a tab canonicalizes to the first authorized tab (MyWorkspace).
    const tabs = getVisibleWorkspaceTabs(user);
    const known = tabs.map(t => t.id);
    const kids = tabs.map((t, i) => item(`/dashboard/workspace?tab=${t.id}`, t.label, t.icon, `ms-nav-ws-${t.id}`, { tab: t.id, defaultTab: i === 0, knownTabs: known }));
    // My Quality Records follows Performance visibility (same roles) — flat item, alphabetical like the rest
    if (known.includes('performance')) kids.push(item('/my-quality-records', 'My Quality Records', ShieldAlert, 'ms-nav-ws-quality-records'));
    if (kids.length) sections.push({ title: 'My Workspace', items: kids.sort(byLabel) });
  }

  if (ANALYTICS_ROLES.includes(role) && !['hr_manager', 'hr_specialist'].includes(role)) {
    const mgmt = ANALYTICS_MGMT.includes(role), targets = TARGET_MANAGER_ROLES.includes(role);
    const a = [];
    if (mgmt) a.push(item('/analytics/attendance', 'Attendance Reports', Clock, 'ms-nav-attendance'), item('/analytics/downtime', 'Downtime Analytics', TrendingDown, 'ms-nav-downtime'));
    a.push(item('/analytics/productivity', 'Productivity Analytics', TrendingUp, 'ms-nav-productivity'));
    if (targets) a.push(item('/analytics/targets', 'Performance Targets', Target, 'ms-nav-targets'), item('/analytics/qc', 'QC Analytics', ShieldCheck, 'ms-nav-qc'));
    sections.push({ title: 'Analytics', items: a.sort(byLabel) });
  }

  if (TEAM_ROLES.includes(role)) {
    if (BREAK_MGMT_ALLOWED_ROLES.includes(role)) {
      // Heading only — the /hr/breaks overview route stays reachable by URL
      sections.push({ title: 'Break Management', items: [
        item('/hr/breaks/active', 'Active Breaks', Activity, 'ms-nav-breaks-active'),
        item('/hr/breaks/history', 'Break History', History, 'ms-nav-breaks-history'),
        item('/hr/breaks/flagged', 'Flagged Breaks', Flag, 'ms-nav-breaks-flagged', { badge: 'flagged_breaks' }),
        item('/hr/breaks/inactivity', 'Inactivity Report', SearchCheck, 'ms-nav-breaks-inactivity'),
      ].sort(byLabel) });
    }

    const tabItem = (t) => item(`/dashboard/team?tab=${t.id}`, t.label, t.icon, `ms-nav-team-${t.id}`, { tab: t.id, defaultTab: false });
    const sessionTab = TEAM_TABS.find(t => t.id === 'session-status');
    const tools = [item('/training/whiteboard?from=dashboard', 'Training Whiteboard', PenLine, 'ms-nav-whiteboard')];
    if (sessionTab) tools.push(tabItem(sessionTab));
    if (!TEAM_LITE_ROLES.includes(role)) tools.push(item('/team/auto-assignment', 'Auto Assignment', Zap, 'ms-nav-auto-assignment'), item('/team/edit-requests', 'Edit Requests', FilePenLine, 'ms-nav-edit-requests'));
    sections.push({ title: 'Operations Tools', items: tools.sort(byLabel) });

    // TEAM-MANAGEMENT-NAV-DUPLICATION-001: the parent "Team Management" link (bare /dashboard/team) duplicated Leave
    // Approvals; only the three tab links remain. Bare /dashboard/team now redirects to ?tab=approvals (App.js).
    const team = TEAM_TABS.filter(t => t.id !== 'session-status').map(tabItem);
    sections.push({ title: 'Team Management', items: team.sort(byLabel) });
  }

  // ADMIN-NAV-001 (Sep 2026): the Admin → Settings entry (→ /admin) was redundant with Dashboard → Home; removed. Route /admin unchanged.
  return sections;
};

const isItemActive = (it, loc) => {
  const [path, qs] = it.to.split('?');
  const tab = new URLSearchParams(loc.search).get('tab');
  if (it.tab && !it.child) return loc.pathname === path && (tab === it.tab || (it.defaultTab && (!tab || !it.knownTabs.includes(tab))));
  if (it.child && it.tab) return loc.pathname === path && tab === it.tab;
  if (it.parentOf === 'tab') return loc.pathname === path && !tab;
  if (it.parentOf === 'path') return loc.pathname === path || loc.pathname === `${path}/`;
  if (it.end) return loc.pathname === path;
  return loc.pathname === path || loc.pathname.startsWith(`${path}/`) || (!!qs && loc.pathname === path);
};

const Item = ({ item, active, badge }) => (
  <Link to={item.to} data-testid={item.testid} title={item.label} aria-current={active ? 'page' : undefined}
    className={`ms-item relative flex items-center rounded-lg text-[13px] whitespace-nowrap max-lg:justify-center max-lg:w-10 max-lg:h-9 max-lg:mx-auto lg:h-8 lg:pr-2 ${item.child ? 'max-lg:hidden lg:pl-9 lg:gap-2 lg:text-[12.5px]' : 'lg:pl-3 lg:gap-2.5'} ${active ? 'is-active text-white font-semibold' : item.child ? 'text-slate-400 hover:text-white hover:bg-white/[.06]' : 'text-slate-300 hover:text-white hover:bg-white/[.07]'}`}>
    <item.icon className={`shrink-0 ${item.child ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${active ? 'text-white' : item.child ? 'text-slate-500' : 'text-slate-400'}`} />
    <span className="truncate max-lg:hidden flex-1">{item.label}</span>
    {badge > 0 && (
      <span className="ms-badge shrink-0 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none min-w-[18px] h-[18px] px-1 shadow-sm max-lg:absolute max-lg:-top-0.5 max-lg:-right-0.5 max-lg:min-w-[14px] max-lg:h-[14px] max-lg:text-[9px]"
        data-testid={`${item.testid}-badge`} aria-label={`${badge} ${item.label}`}>{badge > 99 ? '99+' : badge}</span>
    )}
  </Link>
);

// NAV-GROUPS-001: read-only Flagged Breaks count for the sidebar badge (existing GET /api/breaks/flagged; no new endpoint).
const useFlaggedBreaksBadge = (enabled) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const ctrl = new AbortController();
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/breaks/flagged?page=1&limit=1`, { signal: ctrl.signal, headers: token ? { Authorization: `Bearer ${token}` } : undefined })
      .then(r => setCount(Number(r.data?.total) || 0))
      .catch(() => setCount(0));
    return () => ctrl.abort();
  }, [enabled]);
  return count;
};

// Fixed-width premium sidebar (no user collapse). Below `lg` it renders as a compact icon rail purely for small screens.
export const ModuleShell = ({ children, quote = '“People power performance.”', testId = 'module-shell' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const { permissions } = usePermissions();
  const nav = buildModuleNav(user, deriveWorkspaces(user, permissions), loc.pathname);
  const isIT = ['it_manager', 'it_support'].includes(user?.role);
  const badges = { flagged_breaks: useFlaggedBreaksBadge(nav.some(s => s.items.some(i => i.badge === 'flagged_breaks'))) };
  return (
    <ShellContext.Provider value={true}>
      <div className="min-h-screen bg-slate-50 flex" data-testid={testId}>
        <aside className="ms-sidebar sticky top-0 h-screen shrink-0 flex flex-col text-slate-200 w-[68px] lg:w-[236px]" data-testid="module-sidebar">
          <div className="h-14 shrink-0 flex items-center border-b border-white/10 justify-center px-2 lg:justify-start lg:px-4">
            <TitleFlowLogo variant="darkBright" className="cursor-pointer" style={{ height: 28, width: 'auto' }} onClick={() => navigate(user?.role === 'admin' ? '/admin' : getDefaultRoute(user?.role))} data-testid="module-shell-logo" />
          </div>
          <nav className="ms-nav flex-1 overflow-y-auto overflow-x-hidden px-3 py-3" aria-label="Module navigation">
            {nav.map((sec, si) => (
              <div key={sec.title} className={si ? 'mt-3 pt-3 border-t border-white/[.08]' : ''} data-testid={`ms-section-${sec.title.toLowerCase().replace(/\s+/g, '-')}`}>
                {sec.heading !== false && <div className="max-lg:hidden pl-3 pb-1.5 text-[10px] font-bold tracking-[.14em] uppercase text-slate-500 leading-4">{sec.title}</div>}
                <div className="space-y-px">{sec.items.map(it => <Item key={it.to} item={it} active={isItemActive(it, loc)} badge={it.badge ? badges[it.badge] : 0} />)}</div>
              </div>
            ))}
            {quote && (
              <div className="max-lg:hidden mt-5 rounded-xl border border-white/10 bg-white/[.05] p-3 text-[12px] leading-snug text-slate-300" data-testid="module-shell-quote">
                <Quote className="h-3.5 w-3.5 text-violet-300 mb-1.5" />{quote}<div className="text-[10.5px] text-slate-500 mt-1">— TitleFlow</div>
              </div>
            )}
          </nav>
        </aside>
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="header-frosted sticky top-0 z-40 h-14 flex items-center justify-between gap-3 px-5" data-testid="module-topbar">
            <span className="text-[13px] font-semibold text-slate-700 truncate" data-testid="module-topbar-title"><BarChart3 className="inline h-4 w-4 mr-1.5 text-blue-600 -mt-0.5" />TitleFlow Workspace</span>
            <div className="flex items-center gap-2">
              {!isIT && <><SessionHealthIndicator /><BreakButton /><SystemDowntimeButton /></>}
              <WorkspaceSwitcher user={user} tone="light" />
              <TeamActivityPanel currentUser={user} />
              <NotificationBell user={user} />
              <UserProfileMenu user={user} />
            </div>
          </header>
          <main className="ms-page flex-1 min-w-0 px-5 py-4" data-testid="module-content" key={loc.pathname}>{children}</main>
        </div>
      </div>
    </ShellContext.Provider>
  );
};

/** Wraps <Routes/>: shell paths render inside ONE mounted ModuleShell; everything else (Operations dashboard, admin, etc.) is untouched. */
export const WorkspaceShellGate = ({ children }) => {
  const loc = useLocation();
  return isShellPath(loc.pathname) ? <ModuleShell>{children}</ModuleShell> : children;
};

export default ModuleShell;
